/**
 * The apps of the platform (docs/18-vorratsbuch.md, V0-1): one server, one build, and the address
 * says which app it is. An app is a name, colours, icons and the profiles it shows; the code stays
 * the same. The device picks it from its own host name, so it works without a network too.
 */

export type AppId = "kellerbuch" | "vorratsbuch";

export interface AppDef {
  id: AppId;
  /** Name in the manifest and the title: "KellerLog", on a test instance "KellerLog (Test)" (appForHost). */
  name: string;
  /** A test instance (isTestHost): "(Test)" in the name, „Testbetrieb“ at the foot of the sign-in page. */
  test?: boolean;
  /** Under the icon on the home screen. */
  short: string;
  description: string;
  /** Under the name on the sign-in page. */
  tagline: string;
  /** Colour of the bar and of buttons (theme), its darker shade, and of the start screen (background). */
  theme: string;
  dark: string;
  background: string;
  /** Where the icons lie in the built app: "/icons" or "/icons/vorratsbuch". */
  icons: string;
  /** The profiles this app shows, in order. */
  profiles: readonly string[];
}

export const APPS: Readonly<Record<AppId, AppDef>> = {
  kellerbuch: {
    id: "kellerbuch",
    name: "KellerLog",
    short: "KellerLog",
    description: "Lagerlog – Weinkeller, Bar und Küche, auch ohne Netz",
    tagline: "Getränkekeller und Bar managen, auch ohne Netz",
    theme: "#6b1f2a",
    dark: "#4d141d",
    background: "#f6f0e4",
    icons: "/icons",
    profiles: ["wein", "spirituosen", "bier", "alkoholfrei", "weitere"],
  },
  vorratsbuch: {
    id: "vorratsbuch",
    name: "VorratsBuch",
    short: "VorratsBuch",
    description: "Lagerlog – Vorräte im Haushalt, auch ohne Netz",
    tagline: "Den Vorrat im Blick, auch ohne Netz",
    theme: "#2d5236",
    dark: "#1f3a26",
    background: "#f6f0e4",
    icons: "/icons/vorratsbuch",
    profiles: ["lebensmittel", "haushalt"],
  },
};

/**
 * The app of a host name: VorratsBuch where the first part of the name starts with "vorratsbuch"
 * (vorratsbuch-dev.mzapps.cloud, later vorratsbuch.mzapps.cloud, before the move
 * vorratsbuch-test.zurmuehl.info; vorratsbuch.localhost), KellerLog everywhere else.
 */
export function appForHost(hostname: string): AppDef {
  const first = hostname.toLowerCase().split(".")[0] ?? "";
  const app = first.startsWith("vorratsbuch") ? APPS.vorratsbuch : APPS.kellerbuch;
  return isTestHost(hostname) ? { ...app, name: `${app.name} (Test)`, test: true } : { ...app, test: false };
}

/**
 * A test instance by its host name (the production since 27.10.2026): "-dev" or "-test" at the end
 * of the first part (kellerlog-dev.mzapps.cloud, lagerlog-test.zurmuehl.info), and every local
 * name (localhost, an address, *.localhost, *.test). Everything else is the production.
 */
export function isTestHost(hostname: string): boolean {
  const host = hostname.toLowerCase();
  const first = host.split(".")[0] ?? "";
  return /-(dev|test)$/.test(first) || host === "localhost" || /^[\d.]+$/.test(host) || host.startsWith("[") || /\.(localhost|test)$/.test(host);
}
